import React from "react"
import { graphql, Link } from "gatsby"
import { MDXProvider } from "@mdx-js/react"
import { MDXRenderer } from "gatsby-plugin-mdx"
import { getImage } from "gatsby-plugin-image"

import Seo from "../components/seo"
import Layout from "../components/layout"
import SideBar from "../components/sidebar"
import JumboCard from "../components/jumbocard"
import Video from "../components/video"
import Instructor from "../components/instructor"
import Tabs from "../components/tabs"
import Accordion from "../components/headless-ui/disclosure"
import HorizontalCard from "../components/horizontalcard"


const shortcodes = { Link, Video, Accordion, Tabs, HorizontalCard } // Provide common components here

function Roadmap({ data: {roadmapBanner, mdx, allMdx} }) {
  const topics = mdx.frontmatter.topics;
  const faqs = mdx.exports.faqs;
  const name = mdx.frontmatter.title;
  const icon = getImage(mdx.frontmatter.icon);
  const { edges: courses } = allMdx;
  return (
    <Layout 
      navbar={true}
    >
      <Seo 
        title={mdx.frontmatter.title}
        description={mdx.frontmatter.description}
      />
      <main className="container mx-auto my-20 px-3">
        <JumboCard 
          type={mdx.frontmatter.type}
          title={mdx.frontmatter.title}
          description={mdx.frontmatter.description}
          instructor={mdx.frontmatter.instructor}
          category={mdx.frontmatter.category}
          total={mdx.frontmatter.total}
          icon={icon}
          background={roadmapBanner.childImageSharp.gatsbyImageData.images.fallback.src}
        />
        <section className="flex flex-col lg:flex-row mt-6">
          <div className="lg:w-1/3 lg:mr-4">
            <SideBar 
              list={mdx.frontmatter.list}
              type={mdx.frontmatter.type}
              />
          </div>
          <div className="lg:w-2/3">
            <MDXProvider components={shortcodes}>
              <div className="bg-white p-3 shadow-box rounded-lg">
                <p className="font-medium text-xl text-ia-indigo-dark">Overview</p>
                <div>{mdx.frontmatter.overview}</div>
                <div>
                  <p className="mt-4 text-sm text-ia-blue-default font-medium mb-1">Cover Topics</p>
                  <div className="flex mb-4">
                    {topics.map(topic => <div className="bg-ia-indigo-light text-ia-indigo-default py-1 px-2 rounded mr-2 text-sm" key={topic}>{topic}</div>)}
                  </div>
                </div>
                { mdx.frontmatter.video ? <Video videoSrc={mdx.frontmatter.video} /> : null }
              </div>
              <div className="bg-white p-3 shadow-box rounded-lg my-4">
              <Tabs 
                tabActiveStyles="bg-ia-blue-default text-white font-medium" 
                tabInactiveStyles="bg-blue-gray-100 text-ia-blue-default" 
                tabStyles="p-2"
                tabs="space-x-1 md:space-x-2 "
                contentStyles="mt-2"
              >
                  <div label="Contents">
                    <div className="space-y-4">
                      {courses.filter(({node: course}) => course.frontmatter.roadmapName === name ).map(({ node: course }) => 
                      <HorizontalCard 
                        key={course.id}
                        id={course.id}
                        slug={course.fields.slug}
                        card={course.frontmatter.card}
                        title={course.frontmatter.title}
                        description={course.frontmatter.description}
                        instructor={course.frontmatter.instructor}
                        category={course.frontmatter.category}
                        duration={course.frontmatter.duration}
                      />
                    )}
                    </div>
                  </div>
                  <div label="FAQs">
                    {faqs.map(faq => 
                      <div key={faq.question}>
                        <Accordion title={faq.question}>
                          {faq.answer}
                        </Accordion>
                      </div>
                    )}
                    </div>
                </Tabs>
              </div>
              <div className={`${mdx.body === true ? "bg-white p-3 shadow-box rounded-lg my-4" : "hidden"}`}>
                <MDXRenderer>
                  {mdx.body}
                </MDXRenderer>
              </div>
              <Instructor 
                name={mdx.frontmatter.instructor}
                photo={mdx.frontmatter.instructorImage.childImageSharp.gatsbyImageData.images.fallback.src}
                title={mdx.frontmatter.instructorTitle}
                about={mdx.frontmatter.instructorDescription}
              />
            </MDXProvider>
          </div>
        </section>
      </main>
    </Layout>
  )
}

export const pageQuery = graphql`
  query roadmapQuery($id: String) {
    roadmapBanner: file(relativePath: { eq: "images/roadmap-banner.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: DOMINANT_COLOR)
      }
    }
    allMdx(
      sort: {order: ASC, fields: frontmatter___title}
      filter: {frontmatter: {type: {eq: "course"}}}
    ) {
      edges {
        node {
          frontmatter {
            category
            card {
              childImageSharp {
                gatsbyImageData
              }
            }
            description
            title
            duration
            instructor
            roadmapName
          }
          id
          fields {
            slug
          }
        }
      }
    }
    mdx(id: { eq: $id }) {
      id
      body
      exports {
        faqs {
          answer
          question
        }
      }
      frontmatter{
        title
        description
        overview
        topics
        video
        list
        type
        category
        icon{
          childImageSharp {
            gatsbyImageData
          }
        }
        total
        button
        instructor
        instructorTitle
        instructorImage{
          childImageSharp {
            gatsbyImageData
          }
        }
        instructorDescription
      }
    }
  }
`
export default Roadmap